/**
 * 048 T026 / T044 / T046 / T047 — one workflow: the canvas, the inspector, and its run history.
 *
 * Publishing is the only write that matters, and it is deliberately a separate button from
 * autosave. A draft saves constantly and runs nothing; publishing creates an immutable version and
 * is what live events execute against. Conflating the two is how a half-drawn graph ends up
 * texting customers.
 */
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Plus, Play, Pause, Save, History, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import { AdminOnly } from '@/components/admin-only'
import { btnPrimary, btnSecondary, btnGhost, StatusPill, SkeletonRows } from '@/components/rm'
import { NodeInspector } from '@/components/workflow/inspector'
import { validateGraph, type GraphError, type NodeType, type WorkflowGraph } from '@/lib/workflow-graph'
import { phFormat } from '@/lib/ph'
import {
  useWorkflow, useWorkflowVersions, useWorkflowRuns, useRunSteps, usePublishWorkflow,
  useSetWorkflowEnabled, useSaveDraft, useWorkflowFailures, type StepRow,
} from '@/hooks/use-workflows'

// T001 — lazy, so only this route pays for React Flow.
const Canvas = lazy(() => import('@/components/workflow/canvas'))

export const Route = createFileRoute('/_app/workflows_/$id')({
  component: () => <AdminOnly><WorkflowDetail /></AdminOnly>,
})

const BLANK: WorkflowGraph = {
  schema: 1,
  nodes: [{ id: 'trigger', type: 'trigger', config: {} }],
  edges: [],
}

const ADDABLE: Array<{ type: NodeType; label: string; config: Record<string, unknown> }> = [
  { type: 'condition', label: 'Only if', config: { all: [] } },
  { type: 'branch', label: 'Yes or no', config: { if: { all: [] } } },
  { type: 'wait', label: 'Wait', config: { until: { kind: 'duration', value: 1, unit: 'days' } } },
  { type: 'action', label: 'Do something', config: {} },
]

const when = (iso: string | null) =>
  iso ? phFormat(new Date(iso), 'datetime', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '—'

function WorkflowDetail() {
  const { id } = Route.useParams()
  const nav = useNavigate()
  const isNew = id === 'new'

  const { data: wf, isLoading } = useWorkflow(id)
  const versions = useWorkflowVersions(id)
  const publish = usePublishWorkflow()
  const setEnabled = useSetWorkflowEnabled()
  const saveDraft = useSaveDraft()
  const failures = useWorkflowFailures()

  const [graph, setGraph] = useState<WorkflowGraph>(BLANK)
  const [name, setName] = useState('Untitled workflow')
  const [trigger, setTrigger] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>('trigger')
  const [serverErrors, setServerErrors] = useState<GraphError[]>([])
  const [tab, setTab] = useState<'build' | 'runs'>('build')
  const loaded = useRef(false)

  // Load once. Re-seeding from the query on every refetch would throw away edits made while a
  // background refresh was in flight.
  useEffect(() => {
    if (loaded.current || isNew || !wf) return
    const draft = (wf.trigger_config as { draft?: WorkflowGraph })?.draft
    const published = versions.data?.[0]?.graph
    if (!draft && !published && versions.isLoading) return
    setGraph(draft ?? published ?? BLANK)
    setName(wf.name)
    setTrigger(wf.trigger_event_type)
    loaded.current = true
  }, [wf, versions.data, versions.isLoading, isNew])

  const localErrors = useMemo(() => validateGraph(graph), [graph])
  const errors = serverErrors.length ? serverErrors : localErrors
  const selectedNode = graph.nodes.find((n) => n.id === selected) ?? null

  // Autosave the draft. Only for a workflow that already exists — a brand new one has no row to
  // save into, and creating one silently on first keystroke would litter the list with blanks.
  const dirty = useRef(false)
  const onGraphChange = useCallback((g: WorkflowGraph) => { dirty.current = true; setGraph(g); setServerErrors([]) }, [])
  useEffect(() => {
    if (isNew || !wf || !dirty.current) return
    const t = setTimeout(() => {
      dirty.current = false
      saveDraft.mutate({ id: wf.id, graph, name })
    }, 1500)
    return () => clearTimeout(t)
  }, [graph, name, wf, isNew])   // eslint-disable-line react-hooks/exhaustive-deps

  const addNode = (t: typeof ADDABLE[number]) => {
    const base = t.type
    let n = 1
    while (graph.nodes.some((x) => x.id === `${base}_${n}`)) n++
    const idNew = `${base}_${n}`
    // Connect it to the last node, so a click produces a usable chain rather than an island the
    // validator immediately calls unreachable.
    const tail = [...graph.nodes].reverse().find((x) => x.type !== 'branch') ?? graph.nodes[0]
    setGraph({
      ...graph,
      nodes: [...graph.nodes, { id: idNew, type: base, config: { ...t.config } }],
      edges: tail ? [...graph.edges, { from: tail.id, to: idNew }] : graph.edges,
    })
    setSelected(idNew)
    dirty.current = true
  }

  const updateConfig = (config: Record<string, unknown>) => {
    setGraph({ ...graph, nodes: graph.nodes.map((n) => (n.id === selected ? { ...n, config } : n)) })
    setServerErrors([])
    dirty.current = true
  }

  const deleteNode = () => {
    if (!selected || selected === 'trigger') return
    setGraph({
      ...graph,
      nodes: graph.nodes.filter((n) => n.id !== selected),
      edges: graph.edges.filter((e) => e.from !== selected && e.to !== selected),
    })
    setSelected(null)
    dirty.current = true
  }

  const doPublish = async (enable: boolean) => {
    const r = await publish.mutateAsync({
      workflow_id: isNew ? null : id,
      name,
      trigger_event_type: trigger,
      graph,
      enabled: enable,
    })
    if (r.errors?.length) {
      setServerErrors(r.errors)
      toast.error(r.errors[0].message)
      return
    }
    if (r.error) { toast.error(r.error); return }
    toast.success(enable ? `Published as version ${r.version}. It is live.` : `Saved as version ${r.version}, paused.`)
    if (isNew && r.workflow_id) nav({ to: '/workflows/$id', params: { id: r.workflow_id } })
  }

  if (isLoading && !isNew) return <div className="p-6"><SkeletonRows count={4} /></div>

  return (
    <div className="flex h-[calc(100vh-56px)] flex-col bg-canvas">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 border-b border-token bg-token-surface px-3 py-2.5 sm:px-4">
        <button type="button" className={btnGhost} onClick={() => nav({ to: '/workflows' })} aria-label="Back to workflows">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <input
          className="min-w-0 flex-1 rounded-[10px] border border-transparent bg-transparent px-2 py-1 text-[15px] font-semibold text-token-primary outline-none hover:border-token focus:border-brand"
          value={name}
          aria-label="Workflow name"
          onChange={(e) => { setName(e.target.value); dirty.current = true }}
        />
        {wf && (
          <StatusPill
            status={wf.enabled ? 'active' : 'draft'}
            label={wf.enabled ? 'On' : wf.current_version === 0 ? 'Not published' : 'Paused'}
          />
        )}
        <div className="flex items-center gap-1 rounded-[10px] bg-token-hover p-0.5">
          <TabBtn active={tab === 'build'} onClick={() => setTab('build')}>Build</TabBtn>
          <TabBtn active={tab === 'runs'} onClick={() => setTab('runs')} disabled={isNew}>Runs</TabBtn>
        </div>
        {wf && wf.current_version > 0 && (
          <button
            type="button" className={btnSecondary}
            onClick={() => setEnabled.mutate({ id: wf.id, enabled: !wf.enabled })}
          >
            {wf.enabled ? <><Pause className="h-4 w-4" /> Pause</> : <><Play className="h-4 w-4" /> Turn on</>}
          </button>
        )}
        <button type="button" className={btnSecondary} disabled={publish.isPending} onClick={() => doPublish(false)}>
          <Save className="h-4 w-4" /> Save
        </button>
        <button type="button" className={btnPrimary} disabled={publish.isPending || errors.length > 0} onClick={() => doPublish(true)}>
          Publish and turn on
        </button>
      </div>

      {errors.length > 0 && tab === 'build' && (
        <div className="flex items-start gap-2 border-b border-token bg-token-danger-surface px-4 py-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-token-danger" aria-hidden />
          <p className="text-[12.5px] font-medium text-token-danger">
            {errors[0].message}
            {errors.length > 1 && ` (and ${errors.length - 1} more)`}
          </p>
        </div>
      )}

      {tab === 'build' ? (
        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          <div className="relative min-h-[320px] flex-1">
            <div className="absolute left-3 top-3 z-10 flex flex-wrap gap-1.5">
              {ADDABLE.map((a) => (
                <button
                  key={a.type} type="button"
                  className="inline-flex h-8 items-center gap-1 rounded-[10px] border border-token bg-token-surface px-2.5 text-[12.5px] font-semibold text-token-primary shadow-[0_2px_8px_var(--shadow-color)] hover:bg-token-hover"
                  onClick={() => addNode(a)}
                >
                  <Plus className="h-3.5 w-3.5" /> {a.label}
                </button>
              ))}
            </div>
            <Suspense fallback={<div className="grid h-full place-items-center text-[13px] text-token-secondary">Loading the canvas…</div>}>
              <Canvas
                graph={graph}
                errors={errors}
                selected={selected}
                onSelect={setSelected}
                onGraphChange={onGraphChange}
              />
            </Suspense>
          </div>
          <aside className="min-h-0 w-full shrink-0 overflow-y-auto border-t border-token bg-token-surface lg:w-[340px] lg:border-l lg:border-t-0">
            <NodeInspector
              node={selectedNode}
              errors={errors}
              onChange={updateConfig}
              onDelete={deleteNode}
              triggerEventType={trigger}
              onTriggerChange={(t) => { setTrigger(t); dirty.current = true }}
            />
          </aside>
        </div>
      ) : (
        <RunLog workflowId={id} failures={failures.data ?? []} />
      )}
    </div>
  )
}

function TabBtn({ active, onClick, disabled, children }: { active: boolean; onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button
      type="button" onClick={onClick} disabled={disabled}
      className={`h-7 rounded-[8px] px-2.5 text-[12.5px] font-semibold transition-colors disabled:opacity-40 ${
        active ? 'bg-token-surface text-token-primary shadow-[0_1px_4px_var(--shadow-color)]' : 'text-token-secondary hover:text-token-primary'
      }`}
    >
      {children}
    </button>
  )
}

/** T044 — what it actually did, step by step, including why a step was skipped. */
function RunLog({ workflowId, failures }: { workflowId: string; failures: Array<{ id: number; created_at: string; message: string }> }) {
  const [page, setPage] = useState(0)
  const runs = useWorkflowRuns(workflowId, page)
  const ids = useMemo(() => (runs.data?.rows ?? []).map((r) => r.id), [runs.data])
  const steps = useRunSteps(ids)
  const [open, setOpen] = useState<string | null>(null)

  if (runs.isLoading) return <div className="p-6"><SkeletonRows count={4} /></div>

  if (!runs.data?.rows.length) {
    return (
      <div className="p-8 text-center">
        <History className="mx-auto h-6 w-6 text-token-muted" aria-hidden />
        <p className="mt-2 text-[14px] font-semibold text-token-primary">No runs yet</p>
        <p className="mt-1 text-[13px] text-token-secondary">
          A run appears here the first time a matching event happens. Turn the workflow on if you have not.
        </p>
      </div>
    )
  }

  const total = runs.data.total

  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
      {failures.length > 0 && (
        <div className="mb-4 rounded-[12px] border border-token bg-token-danger-surface p-3.5">
          <p className="text-[13px] font-semibold text-token-danger">Steps that gave up</p>
          <ul className="mt-1.5 space-y-1">
            {failures.slice(0, 5).map((f) => (
              <li key={f.id} className="text-[12.5px] text-token-secondary">
                <span className="text-token-muted">{when(f.created_at)}</span> — {f.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <ul className="divide-y divide-token overflow-hidden rounded-[12px] border border-token bg-token-surface">
        {runs.data.rows.map((r) => {
          const rows = steps.data?.[r.id] ?? []
          const isOpen = open === r.id
          return (
            <li key={r.id}>
              <button
                type="button"
                className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-token-hover"
                onClick={() => setOpen(isOpen ? null : r.id)}
              >
                <StatusPill status={r.status} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13.5px] font-medium text-token-primary">
                    {r.entity_table ? `${r.entity_table} ${r.entity_id}` : 'Run'}
                  </p>
                  <p className="text-[12px] text-token-secondary">
                    Started {when(r.started_at)}
                    {r.finished_at && ` · finished ${when(r.finished_at)}`}
                    {` · ${rows.length} step${rows.length === 1 ? '' : 's'}`}
                  </p>
                </div>
              </button>
              {isOpen && (
                <ol className="space-y-px bg-token-hover px-4 py-3">
                  {rows.map((s) => <StepLine key={s.id} step={s} />)}
                  {!rows.length && <li className="text-[12.5px] text-token-secondary">No steps recorded.</li>}
                </ol>
              )}
            </li>
          )
        })}
      </ul>

      {total > 20 && (
        <div className="mt-3 flex items-center justify-between">
          <button type="button" className={btnGhost} disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Newer</button>
          <span className="text-[12px] text-token-secondary">{page * 20 + 1}–{Math.min((page + 1) * 20, total)} of {total}</span>
          <button type="button" className={btnGhost} disabled={(page + 1) * 20 >= total} onClick={() => setPage((p) => p + 1)}>Older</button>
        </div>
      )}
    </div>
  )
}

/**
 * One step. `skipped` needs a reason more than any other status: a step that did nothing and says
 * nothing is indistinguishable from a bug, which is exactly the support call this page exists to
 * prevent.
 */
function StepLine({ step }: { step: StepRow }) {
  const out = step.output ?? {}
  const reason =
    step.status === 'skipped'
      ? 'The path before it was not taken.'
      : out.skipped === 'no_recipient'
        ? 'Nobody to send to.'
        : out.skipped === 'no_credits'
          ? 'No SMS credits left.'
          : out.passed === 'false'
            ? 'The test did not pass, so the No path ran.'
            : out.passed === 'true'
              ? 'The test passed.'
              : null

  return (
    <li className="flex items-start gap-2.5 rounded-[8px] bg-token-surface px-3 py-2">
      <StatusPill status={step.status} />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium text-token-primary">{step.node_id}</p>
        <p className="text-[11.5px] text-token-secondary">
          {step.node_type}
          {step.attempts > 1 && ` · attempt ${step.attempts}`}
          {step.finished_at ? ` · ${when(step.finished_at)}` : ` · due ${when(step.due_at)}`}
        </p>
        {reason && <p className="mt-0.5 text-[11.5px] text-token-secondary">{reason}</p>}
        {step.error && <p className="mt-0.5 text-[11.5px] font-medium text-token-danger">{step.error}</p>}
        {Array.isArray(out.sent_to) && (out.sent_to as string[]).length > 0 && (
          <p className="mt-0.5 truncate text-[11.5px] text-token-secondary">Sent to {(out.sent_to as string[]).join(', ')}</p>
        )}
      </div>
    </li>
  )
}
